"use client";

import { useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import { useExperimentVariant } from "@/hooks/useExperimentVariant";

// video-kit renders of the whole 3D phone, made by scripts/hero-3d/encode.sh.
// Literal paths so the assets test can see them. A re-render takes a NEW
// filename: /assets/hero-3d is served immutable for a year (infra/scripts/
// deploy.sh), so an overwrite keeps serving the old video.
//
// Two widths per arm. 720 covers the 560px box on a 2x desktop display; `sm` is
// 480 and is what a phone loads. A <source> inside a <video> has no media
// attribute (that is a <picture> feature), so the width is picked here instead.
const CLIPS = {
  // The phone turns slowly the whole time, leaning a little, while €142 counts
  // up and the sheet slides up through the accepted claims; then it speeds up
  // through one full turn and starts over. A seamless 12s loop: the screen
  // resets while the phone shows its back.
  spin: {
    av1: "/assets/hero-3d/phone-spin-left.av1.mp4",
    mp4: "/assets/hero-3d/phone-spin-left.mp4",
    poster: "/assets/hero-3d/phone-spin-left-start.webp",
    smAv1: "/assets/hero-3d/phone-spin-left-sm.av1.mp4",
    smMp4: "/assets/hero-3d/phone-spin-left-sm.mp4",
    smPoster: "/assets/hero-3d/phone-spin-left-sm-start.webp",
    still: "/assets/hero-3d/phone-spin-left-hold.webp",
  },
  // The same film without the full turn: the phone drifts a little sideways,
  // glides back to where it started while the screen fades to dark, and the
  // film restarts behind the dark screen. Phones get their own cut that stands
  // closer: front-on, larger, leaning back and slowly tilting upright.
  sway: {
    av1: "/assets/hero-3d/phone-sway-slow.av1.mp4",
    mp4: "/assets/hero-3d/phone-sway-slow.mp4",
    poster: "/assets/hero-3d/phone-sway-slow-start.webp",
    smAv1: "/assets/hero-3d/phone-sway-tilt-sm.av1.mp4",
    smMp4: "/assets/hero-3d/phone-sway-tilt-sm.mp4",
    smPoster: "/assets/hero-3d/phone-sway-tilt-sm-poster.webp",
    still: "/assets/hero-3d/phone-sway-slow-hold.webp",
  },
} as const;

type Clip = (typeof CLIPS)[keyof typeof CLIPS];

// Read a media query as a store rather than in an effect, so the first client
// render already knows. A reduced-motion visitor must never mount the <video> at
// all: hiding it in CSS would still download it. The width decides which encode
// to fetch, and getting that wrong on the first render costs a second download.
function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false, // server render: the control picture, never the video
  );
}

// The hero's right column, gated by the `hero-3d-video` experiment. Control, SSR,
// no consent and loading all render `children` (the LCP picture), so the indexed
// page and its preload contract are untouched, and nothing here competes with
// the hero image: the variant resolves after hydration, well past LCP.
export function HeroMedia({ children, alt }: { children: ReactNode; alt: string }) {
  const variant = useExperimentVariant("hero-3d-video");
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // Exactly Tailwind's `max-sm:` (below 40rem), so the phone cut and its box
  // switch at the same width.
  const small = useMediaQuery("not all and (min-width: 40rem)");
  // Own keys only: `in` would also take a PostHog variant named "constructor".
  const clip = variant && Object.hasOwn(CLIPS, variant) ? CLIPS[variant as keyof typeof CLIPS] : null;
  if (!clip) return children;

  return (
    <div
      // The phone fills ~70% of the clip's height, so the box runs wider than the
      // phone should read: at 560px the body lands near the control photo's size.
      // 96vw is wider than the padded column on a phone, where mx-auto cannot
      // centre it; the margin centres it on the viewport instead. The sway arm's
      // phone cut stands straight, so a taller box there crops only the dark
      // sides of the video and draws the phone ~18% larger; the negative margin
      // pulls the section up over the dark band below the phone.
      //
      // The box's top edge stays put on purpose. What sits above the fold decides
      // the poster's LCP size, and it is only a few percent under the control
      // photo's: move the box up and a late flag swap reports a new, later LCP.
      // The phone moves up inside the box instead (see SWAY_PHONE_VIDEO).
      className={`relative mx-auto aspect-[9/16] w-[96vw] max-w-[560px] max-sm:ml-[calc(50%-48vw)] lg:-my-10 ${variant === "sway" ? "max-sm:-mb-20 max-sm:aspect-[1/2.1] max-sm:overflow-hidden" : ""}`}
    >
      {reducedMotion ? (
        /* eslint-disable-next-line @next/next/no-img-element -- ~24 KB still, no optimizer round-trip */
        <img
          src={clip.still}
          alt={alt}
          width={720}
          height={1280}
          className="h-full w-full object-cover"
        />
      ) : (
        <LoopVideo
          key={`${variant}-${small}`}
          clip={clip}
          small={small}
          alt={alt}
          className={variant === "sway" ? SWAY_PHONE_VIDEO : ""}
        />
      )}
    </div>
  );
}

// On phones the sway video runs taller than its box and starts above it: the
// clipped strip is the empty band over the phone, so the phone sits higher.
const SWAY_PHONE_VIDEO = "max-sm:-mt-16 max-sm:h-[calc(100%+4rem)]";

// Loads and plays only while the phone is on screen, and pauses when it scrolls
// away: a 12s loop running off screen spends battery on nothing.
//
// No blend mode, mask or filter on the video, deliberately. The hero's ink is
// baked into the frames by the encode script; `mix-blend-mode: lighten` here
// made the browser recomposite the video every frame, and it flickered.
function LoopVideo({
  clip,
  small,
  alt,
  className,
}: {
  clip: Clip;
  small: boolean;
  alt: string;
  className: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    // No observer means no way to tell it is on screen: keep the poster.
    if (!video || !("IntersectionObserver" in window)) return;
    const play = () => {
      video.preload = "auto";
      void video.play()?.catch(() => {});
    };
    const observer = new IntersectionObserver(
      // isIntersecting is true at any sliver, and the first callback fires
      // whatever the ratio, so the threshold alone does not hold 35%.
      ([entry]) => (entry.intersectionRatio >= 0.35 ? play() : video.pause()),
      { threshold: 0.35 },
    );
    // Not a byte of video before the page's own load event: a cached flag can
    // swap the video in during hydration, and it must not compete with the
    // preloaded hero photo that is the page's LCP.
    const start = () => observer.observe(video);
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      observer.disconnect();
    };
  }, []);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="none"
      // The first frame, so the box shows the phone before anything loads.
      poster={small ? clip.smPoster : clip.poster}
      aria-label={alt}
      className={`h-full w-full object-cover ${className}`}
    >
      {/* AV1 first: about half the bytes of the H.264 fallback Safari needs. */}
      <source src={small ? clip.smAv1 : clip.av1} type='video/mp4; codecs="av01.0.05M.08"' />
      <source src={small ? clip.smMp4 : clip.mp4} type="video/mp4" />
    </video>
  );
}
